/*
 * RunApplication.tsx
 *
 * Run the application. Define the configuration that must be used.
 */

import { ResultMessage, ResultStatus } from 'components/messages'
import { ApplicationConfig } from 'services/ApplicationConfig';
import ConfigFactory from 'services/ConfigFactory';
import { setWASMApplicationResults, vector, vector_with_stats } from 'store/wasmApplication/wasmApplicationResult.slice';
import { store } from 'store';
import { IModule } from 'services/ApplicationType';

export default async function RunApplication(config: ApplicationConfig): Promise<ResultMessage> {
    // @ts-ignore
    if (window.CreateWASMApplication === undefined) {
        return {
            'type': ResultStatus.Failure,
            "data": 'window.CreateWASMApplication === undefined',
            // @ts-ignore
            "message": "Scripts still loading... Please try again in a few moments. If this persists, refresh the page."
        }
    }

    // @ts-ignore
    return window.CreateWASMApplication().then((Module) => {
        // @ts-ignore
        if (window.WASMApplcationModule === undefined) {
            // @ts-ignore
            window.WASMApplcationModule = Module;
        }

        try {
            //Do something with the configuration
            console.debug("Module info: ", Module);
            console.debug("Incoming PWA Config: ", config);
            const configClass = ConfigFactory(Module, config);
            Module.setLogLevelError();

            // Run application
            const output = Module.run(configClass, config.nLandslides);

            const results: vector_with_stats = {
                data: [],
                size: output.size(),
                metrics: {
                    rr_thresholds: {
                        median: -9999,
                        perc10: -9999,
                        perc1: -9999
                    }
                },
            };


            function convert_to_array(r: IModule.IVector) {
                var data: number[] = [];
                for (let i = 0; i < r.size(); i++) {
                    data.push(r.get(i));
                }
                return data;

            }

            for (let i = 0; i < output.size(); i++) {
                results.data.push({
                    ID: output.get(i).ID,
                    r: {
                        area: output.get(i).r.area,
                        FOSVeg: output.get(i).r.FOSVeg,
                        FOSNoVeg: output.get(i).r.FOSNoVeg,
                        basalRoot: output.get(i).r.basalRoot,
                        basalRootChar: output.get(i).r.basalRootChar,
                        soilThickness: output.get(i).r.soilThickness,
                        rootLateral: output.get(i).r.rootLateral,
                        rootLateralChar: output.get(i).r.rootLateralChar,
                        vegetationWeight: output.get(i).r.vegetationWeight,
                        densityWet: output.get(i).r.densityWet,
                        cohesion: output.get(i).r.cohesion,
                        cohesionChar: output.get(i).r.cohesionChar,
                        frictionAngle: output.get(i).r.frictionAngle,
                        frictionAngleChar: output.get(i).r.frictionAngleChar,
                        FOSLRR: convert_to_array(output.get(i).r.getFOSLRR()),
                    },
                    s: {
                        area: output.get(i).s.area,
                        FOSVeg: output.get(i).s.FOSVeg,
                        FOSNoVeg: output.get(i).s.FOSNoVeg,
                        basalRoot: output.get(i).s.basalRoot,
                        basalRootChar: output.get(i).s.basalRootChar,
                        soilThickness: output.get(i).s.soilThickness,
                        rootLateral: output.get(i).s.rootLateral,
                        rootLateralChar: output.get(i).s.rootLateralChar,
                        vegetationWeight: output.get(i).s.vegetationWeight,
                        densityWet: output.get(i).s.densityWet,
                        cohesion: output.get(i).s.cohesion,
                        cohesionChar: output.get(i).s.cohesionChar,
                        frictionAngle: output.get(i).s.frictionAngle,
                        frictionAngleChar: output.get(i).s.frictionAngleChar
                    }
                })
            }

            // Update RR Thresholds
            const thresholds = Module.CalculateRRThreshold(output);

            results.metrics.rr_thresholds.median = thresholds.getMedian();
            results.metrics.rr_thresholds.perc10 = thresholds.get10Perc();
            results.metrics.rr_thresholds.perc1 = thresholds.get1Perc();

            output.delete();
            configClass.delete();



            store.dispatch(setWASMApplicationResults(results));

            return {
                'type': ResultStatus.Success,
                'message': '',
                'data': ''
            }
        } catch (err) {
            console.error("Encountered an error while running the application", err);
            return {
                'type': ResultStatus.Failure,
                "data": err,
                // @ts-ignore
                "message": module.getExceptionMessage(err)
            }
        }
    });
}

export async function GetSoils(): Promise<ResultMessage> {
    // @ts-ignore
    if (window.CreateWASMApplication === undefined) {
        return {
            'type': ResultStatus.Failure,
            "data": 'window.CreateWASMApplication === undefined',
            // @ts-ignore
            "message": "Scripts still loading... Please try again in a few moments. If this persists, refresh the page."
        }
    }

    // @ts-ignore
    return window.CreateWASMApplication().then((Module) => {
        const soils = Module.getSoils();
        return {
            'type': ResultStatus.Success,
            "data": soils,
            // @ts-ignore
            "message": "Soils Loaded"
        };
    });
}

export async function GetSoil(soil_name: string): Promise<ResultMessage> {
    // @ts-ignore
    if (window.CreateWASMApplication === undefined) {
        return {
            'type': ResultStatus.Failure,
            "data": 'window.CreateWASMApplication === undefined',
            // @ts-ignore
            "message": "Scripts still loading... Please try again in a few moments. If this persists, refresh the page."
        }
    }

    // @ts-ignore
    return window.CreateWASMApplication().then((Module) => {
        const soil_details = Module.getSoil(soil_name);
        return {
            'type': ResultStatus.Success,
            "data": soil_details,
            // @ts-ignore
            "message": "Soils Loaded"
        };
    });
}


export async function GetTreeNames(): Promise<ResultMessage> {
    // @ts-ignore
    if (window.CreateWASMApplication === undefined) {
        return {
            'type': ResultStatus.Failure,
            "data": 'window.CreateWASMApplication === undefined',
            // @ts-ignore
            "message": "Scripts still loading... Please try again in a few moments. If this persists, refresh the page."
        }
    }

    function get_result(module: IModule.IBase) {
        const species_names = module.getSpeciesHumanReadable();
        return {
            'type': ResultStatus.Success,
            "data": species_names,
            // @ts-ignore
            "message": "Species Loaded"
        };
    }

    // @ts-ignore
    if (window.WASMApplcationModule !== undefined) {
        // @ts-ignore
        return get_result(window.WASMApplcationModule);
    }


    // @ts-ignore
    return window.CreateWASMApplication().then((Module) => {
        // @ts-ignore
        if (window.WASMApplcationModule === undefined) {
            // @ts-ignore
            window.WASMApplcationModule = Module;
        }
        return get_result(Module);
    });
}

export async function GetCanopyCoverBySpecies(config: ApplicationConfig, species_id: number): Promise<ResultMessage> {
    // @ts-ignore
    if (window.CreateWASMApplication === undefined) {
        return {
            'type': ResultStatus.Failure,
            "data": 'window.CreateWASMApplication === undefined',
            // @ts-ignore
            "message": "Scripts still loading... Please try again in a few moments. If this persists, refresh the page."
        }
    }

    function get_result(module: IModule.IBase) {
        // Get the species entry
        const species_obj = module.SpeciesID.from_integral(species_id);
        let output;
        try {
            output = module.CalculateCanopyCoverBySpecies(
                species_obj,
                config.speciesDensity[species_id],
                config.slope * (Math.PI / 180) // Change to radians
            );
        } catch (ex) {
            return {
                'type': ResultStatus.Failure,
                "data": {
                    data: []
                }
                ,
                // @ts-ignore
                "message": module.getExceptionMessage(ex)
            };

        }
        const results: vector = {
            data: [],
            size: output.size()
        };

        for (let i = 0; i < output.size(); i++) {
            results.data.push({
                dbh: output.get(i).dbh,
                mean_stand_density: output.get(i).mean_stand_density,
                canopy_cover: (output.get(i).canopy_cover * 100).toFixed(0),
            });
        }

        output.delete();
        return {
            'type': ResultStatus.Success,
            "data": results,
            // @ts-ignore
            "message": "Canopy Cover Calculated"
        };
    }

    // @ts-ignore
    if (window.WASMApplcationModule !== undefined) {
        try {
            // @ts-ignore
            return get_result(window.WASMApplcationModule);
        } catch (err) {
            // @ts-ignore
            console.error(window.WASMApplcationModule.getExceptionMessage(err));
        }
    }


    // @ts-ignore
    return window.CreateWASMApplication().then((Module) => {
        // @ts-ignore
        if (window.WASMApplcationModule === undefined) {
            // @ts-ignore
            window.WASMApplcationModule = Module;
        }
        return get_result(Module);
    });
}


export async function GetLateralRootReinforcementBySpecies(config: ApplicationConfig, species_id: number): Promise<ResultMessage> {
    // @ts-ignore
    if (window.CreateWASMApplication === undefined) {
        return {
            'type': ResultStatus.Failure,
            "data": 'window.CreateWASMApplication === undefined',
            // @ts-ignore
            "message": "Scripts still loading... Please try again in a few moments. If this persists, refresh the page."
        }
    }

    function get_result(module: IModule.IBase) {

        const species_obj = module.SpeciesID.from_integral(species_id);
        const output = module.CalculateLRRCurvesBySpecies(
            species_obj,
            config.speciesDensity[species_id],
            config.treeDBH[species_id],
            config.slope * (Math.PI / 180) // Change to radians
        );

        const results: vector = {
            data: [],
            size: output.size()
        };

        for (let i = 0; i < output.size(); i++) {
            results.data.push({
                dbh: output.get(i).dbh,
                tree_density: output.get(i).tree_density,
                mean_rr: output.get(i).mean_rr,
            });
        }

        output.delete();
        return {
            'type': ResultStatus.Success,
            "data": results,
            // @ts-ignore
            "message": "LRR Calculated"
        };
    }

    // @ts-ignore
    if (window.WASMApplcationModule !== undefined) {
        try {
            // @ts-ignore
            return get_result(window.WASMApplcationModule);

        } catch (err) {
            // @ts-ignore
            console.error("Encountered an error while running the application", err, window.WASMApplcationModule.getExceptionMessage(err)
            );
            return {
                'type': ResultStatus.Failure,
                "data": err,
                // @ts-ignore
                "message": window.WASMApplcationModule.getExceptionMessage(err)
            }
        }
    }


    // @ts-ignore
    return window.CreateWASMApplication().then((Module) => {
        // @ts-ignore
        if (window.WASMApplcationModule === undefined) {
            // @ts-ignore
            window.WASMApplcationModule = Module;
        }
        return get_result(Module);
    });
}


export async function GetAgeDBHMapBySpecies(config: ApplicationConfig, species_id: number): Promise<ResultMessage> {
    // @ts-ignore
    if (window.CreateWASMApplication === undefined) {
        return {
            'type': ResultStatus.Failure,
            "data": 'window.CreateWASMApplication === undefined',
            // @ts-ignore
            "message": "Scripts still loading... Please try again in a few moments. If this persists, refresh the page."
        }
    }

    function get_result(module: IModule.IBase) {
        const species_obj = module.SpeciesID.from_integral(species_id);
        let output;
        try {
            output = module.CalculateAgeDBHMapBySpecies(
                species_obj,
                config.speciesDensity[species_id],
                config.treeDBH[species_id],
                config.t0,
                config.elevation,
                config.aspectDeg,
                config.slope * (Math.PI / 180) // Change to radians
            );
        } catch (ex) {
            return {
                'type': ResultStatus.Failure,
                "data": {
                    data: []
                },
                // @ts-ignore
                "message": module.getExceptionMessage(ex)
            };
        }

        const results: vector = {
            data: [],
            size: output.size()
        };

        console.log("converting output");
        for (let i = 0; i < output.size(); i++) {
            results.data.push({
                dbh: output.get(i).dbh * 100,
                mean_age: output.get(i).mean_age
            });
        }
        console.log(results);

        output.delete();
        return {
            'type': ResultStatus.Success,
            "data": results,
            // @ts-ignore
            "message": "AgeMap Calculated"
        };
    }

    // @ts-ignore
    if (window.WASMApplcationModule !== undefined) {
        try {
            // @ts-ignore
            return get_result(window.WASMApplcationModule);
        } catch (err) {
            // @ts-ignore
            console.error(window.WASMApplcationModule.getExceptionMessage(err));
        }

    }


    // @ts-ignore
    return window.CreateWASMApplication().then((Module) => {
        // @ts-ignore
        if (window.WASMApplcationModule === undefined) {
            // @ts-ignore
            window.WASMApplcationModule = Module;
        }
        return get_result(Module);
    });
}



export async function CalculateCanopyCoverDBHRRBySpecies(speciesID: number, species_density: number, slope: number): Promise<ResultMessage> {
    // @ts-ignore
    if (window.CreateWASMApplication === undefined) {
        return {
            'type': ResultStatus.Failure,
            "data": 'window.CreateWASMApplication === undefined',
            // @ts-ignore
            "message": "Scripts still loading... Please try again in a few moments. If this persists, refresh the page."
        }
    }


    function get_result(module: IModule.IBase) {
        // Get the species entry
        const species_obj = module.SpeciesID.from_integral(speciesID);
        let output;

        try {

            output = module.CalculateCanopyCoverDBHRRBySpecies(species_obj, slope); //speciesID, density as number, slope as rad

        } catch (err) {

            console.log(err)
            // @ts-ignore
            console.error(module.getExceptionMessage(err));

            return {
                'type': ResultStatus.Failure,
                "data": new Map(),
                // @ts-ignore
                "message": module.getExceptionMessage(err)
            };


        }

        let results = new Map();
        for (let i = 0; i < output.size(); i++) {

            const dbh = output.get(i).dbh;

            if (!results.has(dbh)) {
                results.set(dbh, []);
            }
            results.get(dbh).push({ lrr: output.get(i).lrr, canopy_cover: output.get(i).canopy_cover, alpha: output.get(i).alpha });
        }

        return {
            'type': ResultStatus.Success,
            "data": results,
            // @ts-ignore
            "message": "Soils Loaded"
        };
    }


    // @ts-ignore
    if (window.WASMApplcationModule !== undefined) {
        // @ts-ignore
        return get_result(window.WASMApplcationModule);
    }


    // @ts-ignore
    return window.CreateWASMApplication().then((Module) => {
        // @ts-ignore
        if (window.WASMApplcationModule === undefined) {
            // @ts-ignore
            window.WASMApplcationModule = Module;
        }
        // @ts-ignore
        return get_result(window.WASMApplcationModule);


    });
}




export async function GetCanopyCoverFromLRR_DBH(config: ApplicationConfig, species_id: number): Promise<ResultMessage> {
    // @ts-ignore
    if (window.CreateWASMApplication === undefined) {
        return {
            'type': ResultStatus.Failure,
            "data": 'window.CreateWASMApplication === undefined',
            // @ts-ignore
            "message": "Scripts still loading... Please try again in a few moments. If this persists, refresh the page."
        }
    }

    function get_result(module: IModule.IBase) {
        const species_obj = module.SpeciesID.from_integral(species_id);
        let output;
        try {
            output = module.CalculateCanopyCoverFromLRR_DBH(
                species_obj,
                config.speciesDensity[species_id],
                config.treeDBH[species_id],
                config.slope * (Math.PI / 180) // Change to radians
            );
        } catch (ex) {

            throw Object.assign(new Error("Could not calculate canopy cover from DBH"), {
                'type': ResultStatus.Failure,
                "data": {
                    data: []
                }
                ,
                // @ts-ignore
                "message": "Cannot calculate canopy cover with selected trees"
            });

        }

        return {
            'type': ResultStatus.Success,
            "data": output,
            // @ts-ignore
            "message": "Canopy Cover from LRR _ DBH Calculated"
        };
    }

    // @ts-ignore
    if (window.WASMApplcationModule !== undefined) {
        // @ts-ignore
        return get_result(window.WASMApplcationModule);
    }


    // @ts-ignore
    return window.CreateWASMApplication().then((Module) => {
        // @ts-ignore
        if (window.WASMApplcationModule === undefined) {
            // @ts-ignore
            window.WASMApplcationModule = Module;
        }
        return get_result(Module);
    });
}

